/**
 * /admin/feedback — admin-only feedback inbox.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Star, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { getJSON } from "../../lib/api";
import { usePageMeta } from "../../lib/usePageMeta";
import { AdminHeader } from "./AdminBeta";

const STATUSES = ["", "new", "triaged", "resolved", "wontfix"];
const CATS = ["", "bug", "feature_request", "ux", "other"];

const CAT_COLORS = {
    bug: "bg-accent-red/15 border-accent-red/40 text-accent-red",
    feature_request: "bg-accent-cyan/15 border-accent-cyan/40 text-accent-cyan",
    ux: "bg-accent-amber/15 border-accent-amber/40 text-accent-amber",
    other: "bg-text-dim/10 border-white/[0.08] text-text-muted",
};

const STATUS_COLORS = {
    new: "bg-accent-cyan/15 border-accent-cyan/40 text-accent-cyan",
    triaged: "bg-accent-amber/15 border-accent-amber/40 text-accent-amber",
    resolved: "bg-accent-mint/15 border-accent-mint/40 text-accent-mint",
    wontfix: "bg-text-dim/15 border-white/[0.08] text-text-dim",
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const FeedbackCard = ({ item, onChange }) => {
    const [status, setStatus] = useState(item.status);
    const [notes, setNotes] = useState(item.admin_notes || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const { putJSON } = await import("../../lib/api");
            // PATCH via custom helper — use raw method
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback/${item.id}`,
                {
                    method: "PATCH",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ status, admin_notes: notes }),
                }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success("Updated");
            onChange();
        } catch (e) {
            toast.error(e?.message || "Could not update");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-xl border border-white/[0.06] bg-surface/40 p-4" data-testid={`fb-card-${item.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-wider border ${CAT_COLORS[item.category] || CAT_COLORS.other}`}>
                        {item.category}
                    </span>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={["w-3.5 h-3.5",
                            n <= item.rating ? "fill-accent-amber text-accent-amber" : "text-text-dim",
                        ].join(" ")} />
                    ))}
                    {item.is_beta_tester && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-accent-mint/15 border border-accent-mint/40 text-accent-mint">BETA</span>
                    )}
                </div>
                <div className="text-[11px] text-text-dim text-right font-mono">
                    {fmt(item.created_at)}<br/>
                    <span className="text-text-muted">{item.user_email}</span>
                </div>
            </div>
            <p className="mt-3 text-[13px] text-text-primary leading-relaxed whitespace-pre-wrap">{item.comment}</p>
            <div className="mt-2 text-[10.5px] text-text-dim font-mono">
                {item.page} · {item.viewport || "—"}
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`fb-status-${item.id}`}
                    className="px-2 py-1.5 bg-canvas border border-white/[0.08] rounded-md text-[12px] focus:outline-none focus:border-accent-cyan">
                    {["new", "triaged", "resolved", "wontfix"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={1}
                    placeholder="Admin notes…" data-testid={`fb-notes-${item.id}`}
                    className="px-2 py-1.5 bg-canvas border border-white/[0.08] rounded-md text-[12px] focus:outline-none focus:border-accent-cyan sm:col-span-2" />
            </div>
            <div className="mt-2 flex justify-end">
                <button onClick={save} disabled={saving} data-testid={`fb-save-${item.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-accent-cyan/15 border border-accent-cyan/30 text-accent-cyan text-[11.5px] hover:bg-accent-cyan/25 disabled:opacity-50">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                </button>
            </div>
            <div className={`mt-2 inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${STATUS_COLORS[item.status]}`}>
                {item.status}
            </div>
        </div>
    );
};

export const AdminFeedback = () => {
    usePageMeta({ title: "Feedback inbox — Admin · Cybersecurity Forefront" });

    const [filters, setFilters] = useState({ status: "", category: "", beta: "" });
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.set("status", filters.status);
            if (filters.category) params.set("category", filters.category);
            if (filters.beta) params.set("is_beta_tester", filters.beta);
            const res = await getJSON(`/admin/feedback?${params.toString()}`);
            setData(res);
        } catch (e) {
            toast.error(e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters.status, filters.category, filters.beta]);

    return (
        <div className="min-h-screen bg-canvas text-text-primary" data-testid="admin-feedback-page">
            <AdminHeader />
            <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Admin</p>
                        <h1 className="mt-1 font-display font-semibold text-3xl tracking-tight">Feedback inbox</h1>
                        <p className="mt-1 text-[12.5px] text-text-muted">{data.total} total items</p>
                    </div>
                    <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/[0.08] text-[12.5px] text-text-muted hover:text-accent-cyan">
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                </div>

                <div className="flex gap-2 flex-wrap mb-6 text-[12px]" data-testid="fb-filters">
                    <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        data-testid="fb-filter-status"
                        className="px-2.5 py-1.5 bg-canvas border border-white/[0.08] rounded-md focus:outline-none focus:border-accent-cyan">
                        {STATUSES.map((s) => <option key={s} value={s}>{s || "All status"}</option>)}
                    </select>
                    <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                        data-testid="fb-filter-cat"
                        className="px-2.5 py-1.5 bg-canvas border border-white/[0.08] rounded-md focus:outline-none focus:border-accent-cyan">
                        {CATS.map((s) => <option key={s} value={s}>{s || "All categories"}</option>)}
                    </select>
                    <select value={filters.beta} onChange={(e) => setFilters({ ...filters, beta: e.target.value })}
                        data-testid="fb-filter-beta"
                        className="px-2.5 py-1.5 bg-canvas border border-white/[0.08] rounded-md focus:outline-none focus:border-accent-cyan">
                        <option value="">All users</option>
                        <option value="true">Beta only</option>
                        <option value="false">Non-beta</option>
                    </select>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-text-muted text-[12.5px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                ) : data.items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-white/[0.08] bg-canvas/30 p-12 text-center">
                        <MessageSquare className="w-7 h-7 text-text-dim mx-auto" />
                        <p className="mt-3 text-[13px] text-text-muted">No feedback yet. The first item will land here.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.items.map((it) => <FeedbackCard key={it.id} item={it} onChange={load} />)}
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminFeedback;
